/**
 * Monogram Mapper
 *
 * Handles switching variants based on "Monogram" option selection.
 * Ensures the correct variant (Monogram=Yes vs No) is selected to reflect correct pricing.
 */
class MonogramMapper {
    constructor() {
        this.init();
    }

    init() {
        this.product = this.getProductData();
        if (!this.product) {
            console.warn('MonogramMapper: Product data not found.');
            return;
        }

        this.mapping = this.findMonogramMapping(this.product);
        if (!this.mapping) {
            return;
        }


        this.wireMonogramUI();
        this.setupSubmitHook();
    }

    /**
     * Reads product JSON from <script> tag or window object.
     */
    getProductData() {
        // Try to find the standard Shopify product JSON script
        const jsonScript = document.getElementById('ProductJson-product-template'); // Common ID
        if (jsonScript) {
            try {
                return JSON.parse(jsonScript.textContent);
            } catch (e) {
                console.error('MonogramMapper: Failed to parse ProductJson', e);
            }
        }

        // Fallback: Check for a global object if the theme exposes one (often window.product)
        if (window.product) return window.product;

        // Fallback: Try to find any script with id ending in 'Json' that looks like product data
        const productJson = document.querySelector('[id^="ProductJson-"]');
        if (productJson) {
            try {
                return JSON.parse(productJson.textContent);
            } catch (e) { }
        }

        return null;
    }

    /**
     * Identifies the monogram option and its Yes/No values.
     */
    findMonogramMapping(product) {
        if (!product.options || !product.variants) return null;

        let monogramOptionName = null;
        let monogramOptionPosition = null; // 1, 2, or 3
        let yesValue = null;
        let noValue = null;

        // 1. Look for option name containing "monogram"
        const nameMatch = product.options.find(opt => opt.toLowerCase().includes('monogram'));

        if (nameMatch) {
            // Find position (Shopify options are 'option1', 'option2', 'option3' in variants)
            const index = product.options.indexOf(nameMatch);
            monogramOptionPosition = index + 1;
            monogramOptionName = nameMatch;
        } else {
            // 2. Heuristic: Look for an option with exactly two values including "Yes" and "No"
            const yesNoMatch = product.options.find(optName => {
                // We need to find the values for this option to check
                // Since product.options is just an array of names usually, we scan variants
                const values = new Set();
                const index = product.options.indexOf(optName);
                const positionKey = 'option' + (index + 1);

                product.variants.forEach(v => {
                    if (v[positionKey]) values.add(v[positionKey].toLowerCase());
                });

                return values.has('yes') && values.has('no') && values.size === 2;
            });

            if (yesNoMatch) {
                monogramOptionName = yesNoMatch;
                const index = product.options.indexOf(yesNoMatch);
                monogramOptionPosition = index + 1;
            }
        }

        if (!monogramOptionName) return null;

        // Determine exact "Yes" and "No" values from variants (case-insensitive match)
        const positionKey = 'option' + monogramOptionPosition;
        product.variants.forEach(v => {
            const val = v[positionKey];
            if (!val) return;
            if (val.toLowerCase() === 'yes' && !yesValue) yesValue = val;
            if (val.toLowerCase() === 'no' && !noValue) noValue = val;
        });

        if (!yesValue || !noValue) {
            console.warn('MonogramMapper: Found option name but could not identify Yes/No values.');
            return null;
        }

        return {
            name: monogramOptionName,
            position: monogramOptionPosition,
            yesValue: yesValue, // Capitalized as in DB
            noValue: noValue
        };
    }

    /**
     * Reads current option selections from the theme's form.
     */
    getCurrentSelections() {
        const selections = {};
        const form = document.querySelector('form[action="/cart/add"]');
        if (!form) return selections;

        // 1. Check for radio buttons
        const radios = form.querySelectorAll('input[type="radio"]:checked');
        radios.forEach(radio => {
            const name = radio.name;
            // Typically names are "options[Size]" or similar
            const match = name.match(/options\[(.*?)\]/);
            if (match) {
                selections[match[1]] = radio.value;
            }
        });

        // 2. Check for selects
        const selects = form.querySelectorAll('select');
        selects.forEach(select => {
            const name = select.name;
            const match = name.match(/options\[(.*?)\]/);
            if (match) {
                selections[match[1]] = select.value;
            }
        });

        // If we can't parse options from DOM, fall back to current variant ID
        if (Object.keys(selections).length === 0) {
            const idInput = form.querySelector('input[name="id"]');
            if (idInput && this.product) {
                const currentVariant = this.product.variants.find(v => v.id == idInput.value);
                if (currentVariant) {
                    this.product.options.forEach((optName, index) => {
                        selections[optName] = currentVariant['option' + (index + 1)];
                    });
                }
            }
        }

        return selections;
    }

    /**
     * Returns a target variant based on enabled state of Monogram.
     */
    findTargetVariant(enabled) {
        const currentSelections = this.getCurrentSelections();

        // Target value for the monogram option
        const targetMonogramVal = enabled ? this.mapping.yesValue : this.mapping.noValue;

        // Construct target options array (Shopify expects array of values matching product.options order)
        const targetOptions = this.product.options.map(optName => {
            if (optName === this.mapping.name) {
                return targetMonogramVal;
            }
            return currentSelections[optName];
        });

        return this.findVariantByOptions(targetOptions);
    }

    findVariantByOptions(optionsArray) {
        if (!this.product || !this.product.variants) return null;

        // Normalize comparison
        const targetParams = optionsArray.map(o => (o || '').toLowerCase());

        return this.product.variants.find(variant => {
            const variantOptions = variant.options.map(o => (o || '').toLowerCase());
            // Check for strict equality of all option positions
            return variantOptions.every((val, index) => val === targetParams[index]);
        });
    }

    /**
     * Updates the theme's picker.
     */
    selectVariant(variant) {
        if (!variant) return;


        const form = document.querySelector('form[action="/cart/add"]');
        if (!form) return;

        // Method 1: Try to interact with the theme's visible inputs (Best for UI state consistency)
        let uiUpdated = false;

        // Provide a way to target the specific option input for Monogram
        // Since we know the option name (this.mapping.name), look for inputs
        const escapedName = this.mapping.name.replace(/"/g, '\\"');

        // Try Select
        const select = form.querySelector(`select[name="options[${escapedName}]"]`);
        if (select) {
            // Which value corresponds to this variant's monogram setting?
            const val = variant['option' + this.mapping.position];
            select.value = val;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            uiUpdated = true;
        }

        // Try Radios
        if (!uiUpdated) {
            const val = variant['option' + this.mapping.position];
            // Selectors can be tricky, try simple value match first
            const radio = form.querySelector(`input[type="radio"][value="${val}"]`);
            // Or try name match
            // const radio = form.querySelector(`input[name="options[${escapedName}]"][value="${val}"]`);

            if (radio) {
                radio.checked = true;
                radio.dispatchEvent(new Event('change', { bubbles: true }));
                uiUpdated = true;
            }
        }

        // Method 2: If we couldn't map to UI inputs (or if hidden), update the hidden ID field.
        // This is the "Fallback" from requirements.
        const variantIdInput = form.querySelector('input[name="id"]');
        if (variantIdInput) {
            if (variantIdInput.value != variant.id) {
                variantIdInput.value = variant.id;
                variantIdInput.dispatchEvent(new Event('change', { bubbles: true })); // Trigger theme listeners

                // Also dispatch generic variant change events some themes use
                document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: variant } }));
                document.dispatchEvent(new CustomEvent('product:variant:change', { detail: { variant: variant } }));
            }
        }

        // Update URL if supported by theme (optional nice-to-have)
        const newUrl = new URL(window.location);
        newUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({ path: newUrl.href }, '', newUrl.href);
    }

    /**
     * Listens to the Customizer UI for "Monogram" toggle changes.
     */
    wireMonogramUI() {
        // We need to find the element that the USER interacts with.
        // Based on previous context (customizer-shirts.js), it seems there is a 'monogram' option in `this.styleOptions`.
        // However, the user request asks to wire to "existing monogram toggle".
        // I need to watch for whatever UI element represents "Monogram Yes/No".

        // Strategy: Listen to the document for changes on inputs that might be the customizer toggle,
        // or set up a mutation observer if it's dynamic.

        // Since `customizer-shirts.js` is controlling the UI, I might need to hook into ITS events or inputs.
        // But the requirement says "Add a single new asset... and inclue it... Hook into existing DOM".

        // Assuming the customizer renders inputs for Monogram.
        // Let's assume a generic listener first.

        // Listen to customizer-shirts.js event
        document.addEventListener('monogram:update', (e) => {
            const isEnabled = e.detail.enabled;

            const targetVariant = this.findTargetVariant(isEnabled);
            if (targetVariant) {
                this.selectVariant(targetVariant);
            } else {
                console.warn('MonogramMapper: Could not find matching target variant for Monogram=' + (isEnabled ? 'Yes' : 'No'));
            }
        });

        document.addEventListener('change', (e) => {
            // Check if the changed element is related to Monogram
            const target = e.target;

            // Check for "Monogram" in name, ID, or data attributes
            const isMonogramControl =
                (target.name && target.name.toLowerCase().includes('monogram')) ||
                (target.dataset && target.dataset.name && target.dataset.name.toLowerCase().includes('monogram')) ||
                (target.closest('[data-group="monogram"]'));

            if (isMonogramControl) {
                this.handleMonogramChange(target);
            }
        });

        // Also handle the specific customizer-shirts.js case if possible.
        // It seems that script builds a customized object.
        // If I can't hook into its internal state easily, I might rely on the fact that `customizer-shirts.js` *might* update some hidden input.
        // BUT the prompt says: "Make this theme’s product page(s) switch variants when the user toggles Monogram"

        // Let's try to find if there is a specific toggle we can hook.
        // In `customizer-shirts.js`, options are rendered.
    }

    handleMonogramChange(target) {
        // Determine if "Yes" is selected
        let isEnabled = false;

        if (target.type === 'checkbox') {
            isEnabled = target.checked;
        } else if (target.type === 'radio') {
            isEnabled = (target.value.toLowerCase().includes('yes') || target.value === '1');
        } else if (target.tagName === 'SELECT') {
            isEnabled = (target.value.toLowerCase().includes('yes'));
        } else {
            // Maybe a button or div?
            const val = target.getAttribute('data-value') || target.value;
            isEnabled = (val && val.toLowerCase().includes('yes'));
        }


        const targetVariant = this.findTargetVariant(isEnabled);
        if (targetVariant) {
            this.selectVariant(targetVariant);
        } else {
            console.warn('MonogramMapper: Could not find matching target variant.');
        }
    }

    /**
     * inject properties[Monogram] and properties[monogram_initials]
     */
    setupSubmitHook() {
        const form = document.querySelector('form[action="/cart/add"]');
        if (!form) return;

        form.addEventListener('submit', (e) => {
            // 1. Get current state from Mapper's perspective (or read DOM)
            // We know logical state based on the selected variant
            const currentVariantId = form.querySelector('input[name="id"]')?.value;
            const currentVariant = this.product.variants.find(v => v.id == currentVariantId);

            if (!currentVariant) return;

            const monogramVal = currentVariant['option' + this.mapping.position];
            const isYes = (monogramVal === this.mapping.yesValue);

            // Remove existing hidden inputs to avoid duplicates
            const keys = ['properties[Monogram]', 'properties[monogram_initials]'];
            keys.forEach(k => {
                const inputs = form.querySelectorAll(`input[name="${k}"]`);
                inputs.forEach(i => i.remove());
            });

            if (isYes) {
                // Find initials input
                // Try to find where the user types initials.
                // In customizer-shirts.js, it might be in an input with name "monogram_text" or similar
                const initialsInput = document.querySelector('input[name="monogram_text"], input[data-monogram-text], #monogram-text');
                const initials = initialsInput ? initialsInput.value.trim().toUpperCase() : '';

                // Validation (Req 5): If < 3 chars (assuming required full 3? OR just "present but < 3"? Prompt says "initials present but < 3 chars")
                // Wait, "Do not block add-to-cart if initials are blank when Monogram=No."
                // "If Monogram=Yes and initials present but < 3 chars, prevent submit"

                if (initials.length > 0 && initials.length < 3) {
                    e.preventDefault();
                    alert('Please enter 3 characters for your monogram.'); // Simple validation
                    return;
                }

                // Create hidden inputs
                this.addHiddenInput(form, 'properties[Monogram]', 'Yes');
                if (initials) {
                    this.addHiddenInput(form, 'properties[monogram_initials]', initials);
                }
            } else {
                this.addHiddenInput(form, 'properties[Monogram]', 'No');
            }
        });
    }

    addHiddenInput(form, name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }
}

// Initialize on load
document.addEventListener('DOMContentLoaded', () => {
    window.monogramMapper = new MonogramMapper();
});
